import React, { useState, useEffect, useRef, useCallback } from "react";
import { Editor } from "react-draft-wysiwyg";
import PropTypes from "prop-types";
import {
  EditorState,
  convertToRaw,
  convertFromRaw,
  ContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";

const EmailContentEditor = ({
  initialContent,
  setFieldValue,
  name,
  onChange,
  placeholder,
}) => {
  const [editorState, setEditorState] = useState(
    EditorState.createEmpty()
  );
  const [lastHtml, setLastHtml] = useState("");

  const isInternalChange = useRef(false);
  const debounceTimer = useRef(null);

  // ✅ Load initial HTML or DraftJS raw data
  const loadContent = useCallback((content) => {
    try {
      if (!content) return;

      let contentState;

      if (typeof content === "object" && content.blocks) {
        contentState = convertFromRaw(content);
      } else if (typeof content === "string") {
        const blocksFromHtml = htmlToDraft(content);
        if (blocksFromHtml) {
          const { contentBlocks, entityMap } = blocksFromHtml;
          contentState = ContentState.createFromBlockArray(
            contentBlocks,
            entityMap
          );
        }
      }

      if (contentState) {
        const newEditorState = EditorState.createWithContent(contentState);
        setEditorState(newEditorState);
        setLastHtml(content || "");
      }
    } catch (err) {
      console.error("Error loading content:", err);
    }
  }, []);

  // ✅ componentDidMount
  useEffect(() => {
    if (initialContent) {
      loadContent(initialContent);
    }
  }, [initialContent, loadContent]);

  // ✅ componentDidUpdate (external changes only)
  useEffect(() => {
    if (
      initialContent &&
      !isInternalChange.current &&
      initialContent !== lastHtml
    ) {
      loadContent(initialContent);
    }
  }, [initialContent, lastHtml, loadContent]);

  // ✅ Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  // ✅ Called when user types / edits
  const onEditorStateChange = (state) => {
    isInternalChange.current = true;

    const content = state.getCurrentContent();
    const html = draftToHtml(convertToRaw(content));

    setEditorState(state);
    setLastHtml(html);

    // 🔥 Debounced external sync
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (setFieldValue && name) {
        setFieldValue(name, html);
      }

      if (onChange) {
        onChange({ html });
      }

      isInternalChange.current = false;
    }, 300);
  };

  return (
    <div className="editor-container">
      <Editor
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
        wrapperClassName="wrapper"
        editorClassName="editor border p-2 rounded bg-white"
        handlePastedText={() => "not-handled"}
        stripPastedStyles={false}
        toolbarClassName="toolbar-class hide-toolbar"
        placeholder={placeholder || "Type your text here..."}
        toolbar={{
          options: ["list", "textAlign", "link"],
        }}
      />
    </div>
  );
};

EmailContentEditor.propTypes = {
  initialContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  setFieldValue: PropTypes.func,
  name: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
};

export default EmailContentEditor;
